import {useState} from 'react';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {Box, Center, CenterProps, Container, Flex, FlexProps, Grid, Text} from '@chakra-ui/layout';
import {Button} from '@chakra-ui/button';

import {py} from '@eksab/theme';
import {useTranslate} from '@eksab/i18n';
import {useInfiniteScroll} from '@eksab/hooks';
import {InfiniteScrollingIndicator} from '@eksab/components';
import {RefreshIcon, UnknownErrorIcon} from '@eksab/assets/icons';
import {getLanguage} from '@eksab/util';

import {ChallengeCard, JoinChallengeModal} from '@eksab/features/trivia/components';
import {Filters, useFiltersState} from '@eksab/features/trivia/components/Filters';
import {useChallengesQuery} from '@eksab/features/trivia/hooks';
import {NoChallengesIcon} from '@eksab/features/trivia/icons';
import {RulesIcon} from '@eksab/features/trivia/icons/RulesIcon';
import {RulesModal} from '@eksab/features/trivia/components/modals/RulesModal';
import {WERulesModal} from '@eksab/features/trivia/components/modals/WERulesModal';
import type {ChallengeConfig} from '@eksab/features/play/types';

import {ListingHeader} from './ListingHeader';
import {useEmbedded} from '@eksab/hooks/useEmbedded';

interface ChallengeListProps extends FlexProps {
  game: ChallengeConfig;
}

export function ChallengeList({game}: ChallengeListProps) {
  const [isRulesOpen, setRulesOpen] = useState(false);
  const [filters, setFilters, count] = useFiltersState();
  const {isEmbedded} = useEmbedded();

  const t = useTranslate();
  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetched,
    isError,
    isLoading,
    refetch: refetchChallenges,
  } = useChallengesQuery(game.name, filters);
  useInfiniteScroll({
    action: fetchNextPage,
    condition: !isFetchingNextPage && !!hasNextPage,
    threshold: 0.3,
  });

  const challenges = data?.pages?.flatMap(({data}) => data);
  const hasChallenges = !!challenges?.length;

  const isWe = game.name === 'trivia-misc-we';

  return (
    <>
      <Head>
        <meta property="og:title" content={`${t(`play.game.${game.name}.title`)} | ${t('app.name')}`} />
        <meta property="og:description" content={t(`play.game.${game.name}.description`)} />
        <meta name="description" content={t(`play.game.${game.name}.description`)} />
        <title>{`${t(`play.game.${game.name}.title`)} | ${t('app.name')}`}</title>
      </Head>

      <Container flex={1} flexDir="column" gap={{base: 4, lg: 9}} py={py}>
        <ListingHeader game={game} />

        <Flex flex={1} as="section" flexDir="column" gap={{base: 6, lg: 9}}>
          <Grid
            alignItems="center"
            justifyContent="space-between"
            columnGap="6"
            px={{base: 2, lg: 6}}
            templateColumns="auto auto"
          >
            <div>
              <Button
                h="unset"
                gap="2"
                px="3"
                mb="2"
                py="2"
                _hover={{}}
                _active={{}}
                onClick={() => setRulesOpen(true)}
                bgColor="#FFFEE3"
                color="#877C2B"
                borderColor="#807E5433"
                borderWidth="1px"
                fontSize="sm"
              >
                <RulesIcon />
                <p>{t('trivia.rules.check')}</p>
              </Button>

              <Text as="h2" color="#191b20" fontSize={{base: 'md', md: 'xl', lg: '2xl'}} fontWeight="700">
                {t('trivia.listing.title')}
              </Text>
            </div>

            {!isEmbedded && (
              <Box gridRow="span 2" alignSelf="flex-end">
                <Filters initialState={filters} onSubmit={setFilters} count={count} />
              </Box>
            )}
          </Grid>

          {isLoading || hasChallenges ? (
            <Grid templateColumns={{base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)'}} gap={{base: 3.5, lg: 7}}>
              {isLoading
                ? Array.from({length: 9}).map((_, i) => <ChallengeCard.Skeleton key={i} />)
                : challenges?.map((challenge) => <ChallengeCard key={challenge.id} challenge={challenge} />)}
            </Grid>
          ) : isError ? (
            <ChallengesError refetch={refetchChallenges} flex={1} />
          ) : (
            <NoChallenges flex={1} />
          )}

          {isFetched && hasChallenges && <InfiniteScrollingIndicator isOver={!hasNextPage} mt={[6, 12]} />}
        </Flex>
      </Container>

      {isWe ? (
        <WERulesModal isOpen={isRulesOpen} onClose={() => setRulesOpen(false)} />
      ) : (
        <RulesModal isOpen={isRulesOpen} onClose={() => setRulesOpen(false)} topic={game.topic} />
      )}

      <JoinChallengeModal />
    </>
  );
}

const ChallengesError = ({refetch, ...props}: {refetch: () => void} & CenterProps) => {
  const {locale} = useRouter();

  const t = useTranslate();

  return (
    <Center flexDir="column" textAlign="center" {...props}>
      <UnknownErrorIcon
        width="44"
        height="auto"
        mb="3"
        transform="auto"
        scaleX={getLanguage(locale) === 'en' ? 1 : -1}
      />

      <Text fontSize="2xl" fontWeight="800" mb="0.5">
        {t('app.errors.unknown.heading')}
      </Text>

      <Text fontSize={{base: 'sm', lg: 'md'}} fontWeight="600" color="#8f8f8f" mb="3">
        {t('app.errors.unknown.body')}
      </Text>

      <Button variant="outline" gap="6" px="6" bg="#fafafa" borderWidth="1px" borderColor="#dae2da" onClick={refetch}>
        <Text color="inherit" fontSize="xs" fontWeight="700">
          {t('app.errors.cta.refresh')}
        </Text>

        <RefreshIcon color="inherit" width="5" height="auto" />
      </Button>
    </Center>
  );
};

const NoChallenges = (props: CenterProps) => {
  const {locale} = useRouter();

  const t = useTranslate();

  return (
    <Center flexDir="column" textAlign="center" {...props}>
      <NoChallengesIcon transform="auto" scaleX={getLanguage(locale) === 'en' ? 1 : -1} mb="5" />

      <Text fontSize="2xl" fontWeight="800" mb="1">
        {t('trivia.listing.no-challenges.title')}
      </Text>

      <Text fontSize={{base: 'sm', lg: 'md'}} fontWeight="600" color="#8f8f8f">
        {t('trivia.listing.no-challenges.description')}
      </Text>
    </Center>
  );
};
